import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import Card from "react-bootstrap/Card";
import { Container, Row, Col, Button, Modal, Spinner, FormControl } from "react-bootstrap";
import { dataContext } from "components/dataContext/dataContext";
import DataTable from "react-data-table-component";
import "../assets/css/dataTable.css";

import { AccountContext } from "components/Account/Account";
import consultaDLQ from "api/consultaDLQ";
import { getOrigenLabel } from "constants/origenEmision";

// Respaldo local: si el backend aun no envia origen/etapa (deploy del monitor pendiente),
// la tabla igual muestra nombres legibles en vez del queueName crudo.
const QUEUE_META = {
  DLQ_DB: { origen: "pos", etapa: "Guardar en BD" },
  DLQ_XML: { origen: "pos", etapa: "Generar XML" },
  DLQ_SII: { origen: "pos", etapa: "Enviar al SII" },
  DLQ_WEB_FOLIO: { origen: "web", etapa: "Asignar folio" },
  DLQ_WEB_FIRMA: { origen: "web", etapa: "Firmar XML" },
  DLQ_WEB_BD: { origen: "web", etapa: "Guardar en BD" },
  DLQ_WEB_SII: { origen: "web", etapa: "Enviar al SII" },
  DLQ_WEB_RECEPTOR: { origen: "web", etapa: "Enviar al receptor" },
};

function DLQMonitor() {
  const { data } = useContext(dataContext);
  const { getSession } = useContext(AccountContext);

  const [queues, setQueues] = useState([]);
  const [resumen, setResumen] = useState({});
  const [origenFilter, setOrigenFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [redriving, setRedriving] = useState({});
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState('');
  const handleClose = () => {
    setShow(false)
  };
  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const idToken = await getSession().then((session) => session.idToken.jwtToken);
      // console.log("Fetching DLQ data...");
      const resp = await consultaDLQ.getDLQ(idToken);
      setQueues(resp?.data?.queues ?? []);
      setResumen({
        checkedAt: resp?.data?.checkedAt,
        totalVisible: resp?.data?.totalVisible,
        totalMessages: resp?.data?.totalMessages,
        porOrigen: resp?.data?.porOrigen,
        conError: resp?.data?.conError,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [getSession]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const redriveDLQ = useCallback(
    async (row) => {
      try {
        setShow(true);
        setLoadingModal(true);
        const idToken = await getSession().then((session) => session.idToken.jwtToken);
        // Llama a la API para redrive
        await consultaDLQ.redriveDLQ(row.queueName, idToken)
        // Muestra mensaje de éxito
        setMessage(`Redrive iniciado para la cola ${row.etapa} (${row.queueName}). Por favor, monitorea el estado de las colas para verificar el progreso.`);
        setRedriving((prev) => ({ ...prev, [row.queueName]: true }));
        // refresca conteos
        await fetchQueues();
      } catch (err) {
        console.log(err);
        // El backend ya responde el 400 con el header CORS correcto, asi que su mensaje
        // (con la lista de dlqKey validos) llega al navegador en vez de un error de red.
        const detalle = err?.response?.data?.message;
        setMessage(
          detalle
            ? `Error al iniciar redrive para la cola ${row.queueName}: ${detalle}`
            : `Error al iniciar redrive para la cola ${row.queueName}.`
        );
      } finally {
        setLoadingModal(false);
        setRedriving((prev) => ({ ...prev, [row.queueName]: false }));

      }
    },
    [getSession, fetchQueues]
  );

  // Normaliza cada fila con origen/etapa (los del backend, o los del respaldo local)
  const rows = useMemo(
    () =>
      queues.map((q) => {
        const meta = QUEUE_META[q.queueName] ?? {};
        return {
          ...q,
          origen: q.origen ?? meta.origen ?? 'otro',
          etapa: q.etapa ?? meta.etapa ?? q.queueName,
        };
      }),
    [queues]
  );

  const rowsFiltered = useMemo(
    () => (origenFilter === "all" ? rows : rows.filter((r) => r.origen === origenFilter)),
    [rows, origenFilter]
  );

  const stats = useMemo(() => {
    const totalColas = rows.length;
    let colasConMensajes = 0;
    let mensajesVisibles = 0;
    let colasConError = 0;
    const porOrigen = {};
    rows.forEach((q) => {
      const visible = q.visible ?? 0;
      if (q.error) colasConError += 1;
      if (visible > 0) colasConMensajes += 1;
      mensajesVisibles += visible;
      porOrigen[q.origen] = (porOrigen[q.origen] ?? 0) + visible;
    });
    return {
      totalColas,
      colasConMensajes,
      // El resumen del backend manda cuando viene; si no, se calcula local.
      mensajesVisibles: resumen.totalVisible ?? mensajesVisibles,
      colasConError: resumen.conError ?? colasConError,
      porOrigen: resumen.porOrigen ?? porOrigen,
    };
  }, [rows, resumen]);

  const ultimaActualizacion = useMemo(() => {
    if (!resumen.checkedAt) return data?.lastcheck || '—';
    const fecha = new Date(resumen.checkedAt);
    return isNaN(fecha.getTime()) ? resumen.checkedAt : fecha.toLocaleString('es-CL');
  }, [resumen.checkedAt, data]);

  // Una cola que no se pudo consultar no tiene conteos reales: mostrar 0 mentiria.
  const renderCount = (row, value, extraClass = "") => {
    if (row.error) return <span className="dlq-count dlq-count--unknown">—</span>;
    const n = value ?? 0;
    const alert = extraClass === "" && n > 0 ? " dlq-count--alert" : "";
    return <span className={`dlq-count${extraClass}${alert}`}>{n}</span>;
  };

  const columns = [
    {
      name: "Cola",
      selector: (row) => row.etapa,
      cell: (row) => (
        <div className="dlq-queue">
          <span className="dlq-queue__etapa">
            {row.etapa}
            {row.error && (
              <span className="dlq-badge dlq-badge--error" title={row.error}>
                Sin datos
              </span>
            )}
          </span>
          <span className="dlq-queue__name">{row.queueName}</span>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: '26%',
    },
    {
      name: "Origen",
      selector: (row) => row.origen,
      cell: (row) => (
        <span className={`origen-chip origen-chip--${row.origen}`}>
          {getOrigenLabel(row.origen)}
        </span>
      ),
      sortable: true,
      width: '14%',
    },
    {
      name: "Visible",
      selector: (row) => row.visible ?? 0,
      cell: (row) => renderCount(row, row.visible),
      sortable: true,
      right: true,
      width: '11%',
    },
    {
      name: "No visible",
      selector: (row) => row.notVisible ?? 0,
      cell: (row) => renderCount(row, row.notVisible, " dlq-count--plain"),
      sortable: true,
      right: true,
      width: '12%',
    },
    {
      name: "Delayed",
      selector: (row) => row.delayed ?? 0,
      cell: (row) => renderCount(row, row.delayed, " dlq-count--plain"),
      sortable: true,
      right: true,
      width: '11%',
    },
    {
      name: "Total",
      selector: (row) => row.total ?? 0,
      cell: (row) => renderCount(row, row.total, " dlq-count--strong"),
      sortable: true,
      right: true,
      width: '11%',
    },
    {
      name: "Acciones",
      cell: (row) => {
        const hasMessages = (row.visible ?? 0) > 0;
        const isBusy = !!redriving[row.queueName];

        return (
          <Button
            size="sm"
            className="btn-row btn-row--redrive"
            disabled={!hasMessages || isBusy || !!row.error}
            title={row.error ? "No se pudo consultar esta cola" : undefined}
            onClick={() => redriveDLQ(row)}
          >
            {isBusy ? "Redriving..." : "Redrive"}
          </Button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <Container fluid className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Monitor DLQ</h2>
          <p className="page-subtitle">
            Última actualización: {ultimaActualizacion}
          </p>
        </div>
      </div>

      <Row className="stats-row">
        <Col xs={12} sm={6} lg={3}>
          <div className="stat-card stat-card--info">
            <div className="stat-card__icon"><i className="nc-icon nc-bullet-list-67" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Total colas</span>
              <span className="stat-card__value">{stats.totalColas}</span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className="stat-card stat-card--warning">
            <div className="stat-card__icon"><i className="nc-icon nc-bell-55" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Colas con mensajes</span>
              <span className="stat-card__value">{stats.colasConMensajes}</span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className="stat-card stat-card--danger">
            <div className="stat-card__icon"><i className="nc-icon nc-email-85" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Mensajes visibles</span>
              <span className="stat-card__value">{stats.mensajesVisibles}</span>
              <span className="stat-card__hint">
                POS {stats.porOrigen?.pos ?? 0} · Web {stats.porOrigen?.web ?? 0}
              </span>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <div className={`stat-card ${stats.colasConError > 0 ? 'stat-card--danger' : 'stat-card--success'}`}>
            <div className="stat-card__icon"><i className="nc-icon nc-notification-70" /></div>
            <div className="stat-card__content">
              <span className="stat-card__label">Colas no consultadas</span>
              <span className="stat-card__value">{stats.colasConError}</span>
              <span className="stat-card__hint">
                {stats.colasConError > 0 ? 'Sus conteos no son confiables' : 'Todas respondieron'}
              </span>
            </div>
          </div>
        </Col>
      </Row>

      <Card className="data-card">
        <Card.Body>
          <div className="data-card__toolbar">
            <span className="toolbar-meta">
              {rowsFiltered.length} cola{rowsFiltered.length === 1 ? '' : 's'} monitoreada{rowsFiltered.length === 1 ? '' : 's'}
              {origenFilter !== 'all' && (
                <span className="toolbar-meta__selected">de {rows.length}</span>
              )}
            </span>
            <div className="toolbar-actions">
              <FormControl
                as="select"
                value={origenFilter}
                onChange={(e) => setOrigenFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Todos los orígenes</option>
                <option value="pos">POS</option>
                <option value="web">Emisión web</option>
              </FormControl>
              <Button
                className="btn-export btn-refresh"
                onClick={fetchQueues}
                disabled={loading}
              >
                <i className="nc-icon nc-refresh-69" />
                {loading ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </div>
          </div>
          <DataTable
            className="-striped -highlight"
            fixedHeader
            columns={columns}
            data={rowsFiltered}
            progressPending={loading}
            persistTableHead
          />
        </Card.Body>
      </Card>
      {/* Modal redrive */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>Redrive DLQ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingModal && <Spinner animation="border" role="status" className='sticky-top'></Spinner>}
          <p>{!loadingModal && <> {message} </>}</p>
        </Modal.Body>
        <Modal.Footer>
          {!loadingModal && <Button variant="secondary" onClick={handleClose}>
            Cerrar
          </Button>}

        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default DLQMonitor;
